class Accordion extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.allowMultiple = true;
    this.isConnectedToDom = false;
    this.desktopQuery = null;
    this.onItemToggle = this.onItemToggle.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);
  }

  async connectedCallback() {
    this.isConnectedToDom = true;
    this.allowMultiple = this.dataset.allowMultiple !== 'false';

    await customElements.whenDefined('theme-accordion-item');

    if (!this.isConnectedToDom) {
      return;
    }

    this.items = Array.from(this.querySelectorAll('theme-accordion-item'));

    this.items.forEach((item) => {
      item.addEventListener('accordion:item-toggle', this.onItemToggle);
    });

    this.initializeItems();

    if (this.hasAttribute('data-footer-menu-accordion')) {
      this.setupFooterMenuBehavior();
    }
  }

  disconnectedCallback() {
    this.isConnectedToDom = false;
    this.items.forEach((item) => {
      item.removeEventListener('accordion:item-toggle', this.onItemToggle);
    });

    if (this.desktopQuery) {
      if (typeof this.desktopQuery.removeEventListener === 'function') {
        this.desktopQuery.removeEventListener('change', this.onDesktopChange);
      } else {
        this.desktopQuery.removeListener(this.onDesktopChange);
      }
      this.desktopQuery = null;
    }
  }

  initializeItems() {
    const defaultOpenItems = this.items.filter((item) => item.isDefaultOpen());

    if (!this.allowMultiple && defaultOpenItems.length > 0) {
      const firstOpen = defaultOpenItems[0];
      this.items.forEach((item) => {
        item.setOpen(item === firstOpen, { silent: true });
      });
      return;
    }

    this.items.forEach((item) => {
      item.setOpen(item.isDefaultOpen(), { silent: true });
    });
  }

  onItemToggle(event) {
    if (this.allowMultiple || !event.detail.open) {
      return;
    }

    const toggledItem = event.target;
    this.items.forEach((item) => {
      if (item !== toggledItem) {
        item.setOpen(false, { silent: true });
      }
    });
  }

  setupFooterMenuBehavior() {
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.syncFooterMenuBehavior();

    if (typeof this.desktopQuery.addEventListener === 'function') {
      this.desktopQuery.addEventListener('change', this.onDesktopChange);
    } else {
      this.desktopQuery.addListener(this.onDesktopChange);
    }
  }

  onDesktopChange() {
    this.syncFooterMenuBehavior();
  }

  syncFooterMenuBehavior() {
    if (!this.desktopQuery) {
      return;
    }

    const isDesktop = this.desktopQuery.matches;

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const shouldBeOpen = isDesktop || item.isDefaultOpen();

      item.setOpen(shouldBeOpen, { silent: true });

      if (!trigger) {
        return;
      }

      if (isDesktop) {
        trigger.setAttribute('aria-disabled', 'true');
        trigger.setAttribute('tabindex', '-1');
        return;
      }

      trigger.removeAttribute('aria-disabled');
      trigger.removeAttribute('tabindex');
    });
  }
}

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.trigger = null;
    this.panel = null;
    this.onTriggerClick = this.onTriggerClick.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-accordion-trigger]');
    this.panel = this.querySelector('[data-accordion-panel]');

    if (!this.trigger || !this.panel) {
      return;
    }

    this.setupA11y();
    this.isOpen = this.hasAttribute('open');
    this.syncA11yState();
    this.panel.style.overflow = 'hidden';
    this.trigger.addEventListener('click', this.onTriggerClick);
  }

  disconnectedCallback() {
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.onTriggerClick);
    }
  }

  setupA11y() {
    if (!this.id) {
      this.id = `accordion-item-${Math.random().toString(36).slice(2, 10)}`;
    }

    if (!this.trigger.id) {
      this.trigger.id = `${this.id}-trigger`;
    }

    if (!this.panel.id) {
      this.panel.id = `${this.id}-panel`;
    }

    this.trigger.setAttribute('aria-controls', this.panel.id);
    this.panel.setAttribute('role', 'region');
    this.panel.setAttribute('aria-labelledby', this.trigger.id);
  }

  isDefaultOpen() {
    return this.dataset.defaultOpen === 'true';
  }

  onTriggerClick() {
    this.setOpen(!this.isOpen);
  }

  setOpen(nextState, options = {}) {
    const { silent = false } = options;
    this.isOpen = Boolean(nextState);

    if (!this.trigger || !this.panel) {
      return;
    }
    this.toggleAttribute('open', this.isOpen);
    this.syncA11yState();

    if (!silent) {
      this.dispatchEvent(
        new CustomEvent('accordion:item-toggle', {
          bubbles: true,
          detail: {
            open: this.isOpen,
          },
        }),
      );
    }
  }

  syncA11yState() {
    this.trigger.setAttribute('aria-expanded', String(this.isOpen));

    if (this.isOpen) {
      this.panel.removeAttribute('aria-hidden');
      this.panel.removeAttribute('inert');
      return;
    }

    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.setAttribute('inert', '');
  }
}

if (!customElements.get('theme-accordion')) {
  customElements.define('theme-accordion', Accordion);
}

if (!customElements.get('theme-accordion-item')) {
  customElements.define('theme-accordion-item', AccordionItem);
}
